/**
 * Safety Observation Drawer
 * Create / Edit safety observations.
 */

import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DatePicker, Input, SearchableDropdown, TextArea } from '../forms';
import { useToast } from '../../contexts/ToastContext';
import {
  useCreateObservationMutation,
  useUpdateObservationMutation,
} from '../../services/safetyApi';
import type {
  SafetyObservation,
  ObservationCategory,
  ObservationSeverity,
  CreateObservationRequest,
  UpdateObservationRequest,
} from '../../types/safety';

// ============================================================================
// Constants
// ============================================================================

const CATEGORY_OPTIONS = [
  { value: 'safe_behavior', label: 'Safe Behavior' },
  { value: 'unsafe_behavior', label: 'Unsafe Behavior' },
  { value: 'unsafe_condition', label: 'Unsafe Condition' },
  { value: 'near_miss', label: 'Near Miss' },
  { value: 'positive', label: 'Positive Observation' },
];

const SEVERITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
];

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

// ============================================================================
// Props
// ============================================================================

interface SafetyObservationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  observation?: SafetyObservation | null;
  mode: 'create' | 'edit';
}

// ============================================================================
// Helpers
// ============================================================================

const getSeverityColor = (severity: ObservationSeverity): string => {
  switch (severity) {
    case 'critical':
      return 'bg-red-100 text-red-700 border-red-300';
    case 'high':
      return 'bg-orange-100 text-orange-700 border-orange-300';
    case 'medium':
      return 'bg-yellow-100 text-yellow-700 border-yellow-300';
    case 'low':
      return 'bg-green-100 text-green-700 border-green-300';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

// ============================================================================
// Component
// ============================================================================

const SafetyObservationDrawer: React.FC<SafetyObservationDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  observation,
  mode,
}) => {
  const toast = useToast();

  // Form state
  const [observationDate, setObservationDate] = useState('');
  const [observedByName, setObservedByName] = useState('');
  const [location, setLocation] = useState('');
  const [category, setCategory] = useState<ObservationCategory>('unsafe_condition');
  const [description, setDescription] = useState('');
  const [severity, setSeverity] = useState<ObservationSeverity>('medium');
  const [actionTaken, setActionTaken] = useState('');
  const [followUpRequired, setFollowUpRequired] = useState(false);
  const [followUpDueDate, setFollowUpDueDate] = useState('');
  const [assignedToName, setAssignedToName] = useState('');
  const [status, setStatus] = useState<string>('open');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [createObservation, { isLoading: isCreating }] = useCreateObservationMutation();
  const [updateObservation, { isLoading: isUpdating }] = useUpdateObservationMutation();

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (isOpen && observation && mode === 'edit') {
      setObservationDate(observation.observationDate?.split('T')[0] || '');
      setObservedByName(observation.observedByName || '');
      setLocation(observation.location || '');
      setCategory(observation.category);
      setDescription(observation.description || '');
      setSeverity(observation.severity);
      setActionTaken(observation.actionTaken || '');
      setFollowUpRequired(observation.followUpRequired || false);
      setFollowUpDueDate(observation.followUpDueDate?.split('T')[0] || '');
      setAssignedToName(observation.assignedToName || '');
      setStatus(observation.status);
      setResolutionNotes(observation.resolutionNotes || '');
    } else if (isOpen && mode === 'create') {
      setObservationDate(new Date().toISOString().split('T')[0]);
      setObservedByName('');
      setLocation('');
      setCategory('unsafe_condition');
      setDescription('');
      setSeverity('medium');
      setActionTaken('');
      setFollowUpRequired(false);
      setFollowUpDueDate('');
      setAssignedToName('');
      setStatus('open');
      setResolutionNotes('');
      setErrors({});
    }
  }, [isOpen, observation, mode]);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!observationDate) errs.observationDate = 'Observation date is required';
    if (!location.trim()) errs.location = 'Location is required';
    if (!description.trim()) errs.description = 'Description is required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (mode === 'create') {
        const payload: CreateObservationRequest = {
          observationDate,
          observedByName: observedByName || undefined,
          location,
          category,
          description,
          severity,
          actionTaken: actionTaken || undefined,
          followUpRequired,
          followUpDueDate: followUpRequired && followUpDueDate ? followUpDueDate : undefined,
          assignedToName: assignedToName || undefined,
        };
        await createObservation({ projectId, data: payload }).unwrap();
        toast.success('Observation created successfully');
      } else if (observation) {
        const payload: UpdateObservationRequest = {
          observationDate,
          location,
          category,
          description,
          severity,
          actionTaken: actionTaken || undefined,
          followUpRequired,
          followUpDueDate: followUpRequired && followUpDueDate ? followUpDueDate : undefined,
          assignedToName: assignedToName || undefined,
          status: status as SafetyObservation['status'],
          resolutionNotes: resolutionNotes || undefined,
        };
        await updateObservation({
          projectId,
          observationId: observation._id,
          data: payload,
        }).unwrap();
        toast.success('Observation updated successfully');
      }

      onClose();
    } catch (err: unknown) {
      const message =
        err &&
        typeof err === 'object' &&
        'data' in err &&
        typeof (err as { data?: { message?: string } }).data?.message === 'string'
          ? (err as { data: { message: string } }).data.message
          : 'Failed to save observation';
      toast.error(message);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - 60% width */}
      <div className="fixed top-6 right-6 bottom-6 z-[100] w-[60%] animate-in slide-in-from-right">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {mode === 'create' ? 'New Safety Observation' : 'Edit Observation'}
              </h2>
              {observation && (
                <p className="text-xs text-gray-500 mt-0.5">{observation.observationNumber}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Severity quick-select bar */}
          <div className="px-8 py-3 border-b border-gray-100 flex items-center gap-3">
            <span className="text-xs text-gray-500 font-medium">Severity:</span>
            {(['low', 'medium', 'high', 'critical'] as ObservationSeverity[]).map(s => (
              <button
                key={s}
                onClick={() => setSeverity(s)}
                className={`px-3 py-1 text-xs rounded-full border font-medium transition-colors capitalize ${
                  severity === s
                    ? getSeverityColor(s)
                    : 'border-gray-200 text-gray-400 hover:border-gray-300'
                }`}
              >
                {s}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-5">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Observation Date <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  value={observationDate}
                  onChange={setObservationDate}
                  placeholder="Select date"
                />
                {errors.observationDate && (
                  <p className="text-xs text-red-500 mt-1">{errors.observationDate}</p>
                )}
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Category <span className="text-red-500">*</span>
                </label>
                <SearchableDropdown
                  placeholder="Select category"
                  value={category}
                  onChange={val => setCategory(val as ObservationCategory)}
                  options={CATEGORY_OPTIONS}
                  searchable={false}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Observed By</label>
                <Input
                  value={observedByName}
                  onChange={e => setObservedByName(e.target.value)}
                  placeholder="Observer's name"
                />
              </div>

              <div>
                <label className="block text-xs text-gray-500 mb-1">
                  Location <span className="text-red-500">*</span>
                </label>
                <Input
                  value={location}
                  onChange={e => setLocation(e.target.value)}
                  placeholder="Where was this observed?"
                />
                {errors.location && (
                  <p className="text-xs text-red-500 mt-1">{errors.location}</p>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">
                Description <span className="text-red-500">*</span>
              </label>
              <TextArea
                value={description}
                onChange={setDescription}
                placeholder="Describe what was observed in detail..."
                rows={4}
              />
              {errors.description && (
                <p className="text-xs text-red-500 mt-1">{errors.description}</p>
              )}
            </div>

            <div>
              <label className="block text-xs text-gray-500 mb-1">Immediate Action Taken</label>
              <TextArea
                value={actionTaken}
                onChange={setActionTaken}
                placeholder="What action was taken immediately after this was observed?"
                rows={3}
              />
            </div>

            {/* Follow-up Section */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="followUpRequired"
                  checked={followUpRequired}
                  onChange={e => setFollowUpRequired(e.target.checked)}
                  className="h-4 w-4 text-brand-600 border-gray-300 rounded"
                />
                <label htmlFor="followUpRequired" className="text-sm text-gray-700 font-medium">
                  Follow-up action required
                </label>
              </div>

              {followUpRequired && (
                <div className="grid grid-cols-2 gap-4 pt-1">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Due Date</label>
                    <DatePicker
                      value={followUpDueDate}
                      onChange={setFollowUpDueDate}
                      placeholder="Follow-up due date"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Assigned To</label>
                    <Input
                      value={assignedToName}
                      onChange={e => setAssignedToName(e.target.value)}
                      placeholder="Person responsible"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Status (edit mode only) */}
            {mode === 'edit' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Status</label>
                  <SearchableDropdown
                    placeholder="Select status"
                    value={status}
                    onChange={setStatus}
                    options={STATUS_OPTIONS}
                    searchable={false}
                  />
                </div>
              </div>
            )}

            {/* Resolution notes (edit mode only) */}
            {mode === 'edit' && (
              <div>
                <label className="block text-xs text-gray-500 mb-1">Resolution Notes</label>
                <TextArea
                  value={resolutionNotes}
                  onChange={setResolutionNotes}
                  placeholder="Notes about how this was resolved..."
                  rows={3}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? 'Saving...'
                : mode === 'create'
                ? 'Record Observation'
                : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default SafetyObservationDrawer;
